import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, TrendUp, TrendDown, CircleNotch, ArrowsClockwise, Sparkle } from "@phosphor-icons/react";
import PremiumLock from "./PremiumLock";

const RATING_STYLES = {
  Strong: {
    bg: "bg-pos-soft",
    border: "border-pos-soft",
    text: "text-pos",
    label: "Fundamentally Strong",
  },
  Neutral: {
    bg: "bg-mod-soft",
    border: "border-mod-soft",
    text: "text-mod",
    label: "Fundamentally Neutral",
  },
  Weak: {
    bg: "bg-neg-soft",
    border: "border-neg-soft",
    text: "text-neg",
    label: "Fundamentally Weak",
  },
};

const fmt = (n, digits = 2) =>
  typeof n === "number" && !isNaN(n) ? n.toLocaleString("en-IN", { maximumFractionDigits: digits, minimumFractionDigits: 0 }) : "—";

const pct = (n) => (typeof n === "number" && !isNaN(n) ? `${(n * 100).toFixed(2)}%` : "—");

const mcap = (n) => {
  if (typeof n !== "number" || isNaN(n)) return "—";
  if (n >= 1e12) return `₹${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e10) return `₹${(n / 1e7).toFixed(0).toLocaleString("en-IN")} Cr`;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  return `₹${fmt(n)}`;
};

export default function FundamentalsPanel({ symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [locked, setLocked] = useState(false);

  const load = async (force = false) => {
    setLoading(true);
    setErr("");
    setLocked(false);
    try {
      const { data } = await api.get(`/fundamentals/${encodeURIComponent(symbol)}${force ? "?force=true" : ""}`);
      setData(data);
    } catch (e) {
      if (e.response?.status === 402) {
        setLocked(true);
      } else {
        setErr(e.response?.data?.detail || e.message || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setErr("");
    setLocked(false);
  }, [symbol]);

  if (locked) {
    return (
      <div className="mt-6" data-testid="fundamentals-locked">
        <PremiumLock
          title="AI Fundamental Analysis"
          subtitle="Gemini rates this company on ROE, margins, growth & leverage and gives you a clear Strong / Neutral / Weak verdict."
          featureBullets={[
            "AI-written bullish & bearish points",
            "All key ratios: PE, P/B, ROE, margins, growth",
            "Updated cache — no repeated AI costs",
          ]}
        />
      </div>
    );
  }

  if (!data && !loading && !err) {
    return (
      <div className="mt-6 p-5 border border-subtle rounded-sm bg-surface">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-primary-fg font-medium">
              <Sparkle size={16} weight="fill" className="text-mod" />
              AI Fundamental Check
            </div>
            <p className="text-sm text-secondary-fg mt-1">
              Ask Gemini to rate this company on ROE, margins, growth &amp; leverage — and flag
              it as <span className="text-pos">Strong</span>, <span className="text-mod">Neutral</span>{" "}
              or <span className="text-neg">Weak</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(false)}
            data-testid="run-ai-analysis"
            className="shrink-0 px-4 py-2 rounded-sm font-mono text-[10px] uppercase tracking-widest flex items-center gap-2"
            style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
          >
            <Brain size={14} weight="bold" /> Run Analysis
          </button>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="mt-6 p-5 border border-subtle rounded-sm bg-surface flex items-center gap-3">
        <CircleNotch size={16} weight="bold" className="animate-spin text-mod" />
        <div className="text-sm text-secondary-fg">
          Crunching fundamentals with Gemini 3 Flash…
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="mt-6 p-5 border border-neg-soft bg-neg-soft rounded-sm text-neg text-sm">
        Could not load fundamentals: {err}
      </div>
    );
  }

  const a = data?.analysis || {};
  const f = data?.fundamentals || {};
  const style = RATING_STYLES[a.rating] || RATING_STYLES.Neutral;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 border border-subtle rounded-sm overflow-hidden"
      data-testid="fundamentals-panel"
    >
      {/* Header / Rating */}
      <div className={`p-5 ${style.bg} border-b ${style.border}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className={`flex items-center gap-2 ${style.text} font-medium`}>
              <Sparkle size={14} weight="fill" /> AI Verdict
            </div>
            <div className={`font-mono text-2xl tracking-tight mt-1 ${style.text}`}>
              {a.rating}{" "}
              <span className="text-sm opacity-70">· score {a.score}/10</span>
            </div>
            {a.summary && (
              <div className="text-sm text-primary-fg/90 mt-2 max-w-2xl">{a.summary}</div>
            )}
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            title="Re-run AI analysis"
            className="shrink-0 p-2 border border-subtle rounded-sm bg-surface hover:bg-surface-hover text-muted-fg hover:text-primary-fg"
          >
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Pros/Cons */}
      <div className="grid grid-cols-1 md:grid-cols-2 border-b border-subtle">
        <div className="p-5 border-r border-subtle">
          <div className="text-[10px] font-mono uppercase tracking-widest text-pos mb-3 flex items-center gap-1">
            <TrendUp size={12} weight="bold" /> Bullish
          </div>
          {a.bullish_points?.length ? (
            <ul className="space-y-2 text-sm text-primary-fg/90">
              {a.bullish_points.map((p) => (
                <li key={`bull-${p}`} className="flex gap-2">
                  <span className="text-pos mt-0.5">+</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-muted-fg">—</div>
          )}
        </div>
        <div className="p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-neg mb-3 flex items-center gap-1">
            <TrendDown size={12} weight="bold" /> Bearish
          </div>
          {a.bearish_points?.length ? (
            <ul className="space-y-2 text-sm text-primary-fg/90">
              {a.bearish_points.map((p) => (
                <li key={`bear-${p}`} className="flex gap-2">
                  <span className="text-neg mt-0.5">−</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-muted-fg">—</div>
          )}
        </div>
      </div>

      {/* Raw fundamentals grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 text-sm">
        {[
          { l: "Sector", v: f.sector || "—" },
          { l: "Industry", v: f.industry || "—" },
          { l: "Market Cap", v: mcap(f.marketCap) },
          { l: "Trailing PE", v: fmt(f.trailingPE) },
          { l: "Forward PE", v: fmt(f.forwardPE) },
          { l: "P/B", v: fmt(f.priceToBook) },
          { l: "ROE", v: pct(f.returnOnEquity) },
          { l: "ROA", v: pct(f.returnOnAssets) },
          { l: "Profit Margin", v: pct(f.profitMargins) },
          { l: "Op Margin", v: pct(f.operatingMargins) },
          { l: "Rev Growth", v: pct(f.revenueGrowth) },
          { l: "EPS Growth", v: pct(f.earningsGrowth) },
          { l: "D/E", v: fmt(f.debtToEquity) },
          { l: "Beta", v: fmt(f.beta) },
          { l: "Dividend Yield", v: pct(f.dividendYield) },
          { l: "Street Reco", v: (f.recommendationKey || "—").replace("_", " ") },
        ].map((it) => (
          <div key={it.l} className="p-3 border-r border-b border-subtle last:border-r-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
              {it.l}
            </div>
            <div className="font-mono text-sm tabular-nums text-primary-fg mt-0.5">{it.v}</div>
          </div>
        ))}
      </div>

      <div className="p-2 text-[10px] font-mono uppercase tracking-widest text-muted-fg text-right">
        powered by gemini · cached 24h · data via yfinance
      </div>
    </motion.div>
  );
}
